import { Component } from '@theme/component';
import { fetchConfig, preloadImage, onAnimationEnd, yieldToMainThread } from '@theme/utilities';
import { cartPerformance } from '@theme/performance';
import { morph } from '@theme/morph';
import { CartLinesUpdateEvent, CartErrorEvent, ProductSelectEvent, StandardEvents } from '@shopify/events';

// Error message display duration - gives users time to read the message
const ERROR_MESSAGE_DISPLAY_DURATION = 10000;

// Button re-enable delay after error - prevents rapid repeat attempts
const ERROR_BUTTON_REENABLE_DELAY = 1000;

// Success message display duration for screen readers
const SUCCESS_MESSAGE_DISPLAY_DURATION = 5000;

/**
 * @typedef {HTMLElement & {
 *   source: Element,
 *   destination: Element,
 *   useSourceSize: string | boolean
 * }} FlyToCart
 */

/**
 * A custom element that manages an add to cart button.
 *
 * @typedef {object} AddToCartRefs
 * @property {HTMLButtonElement} addToCartButton - The add to cart button.
 * @extends Component<AddToCartRefs>
 */
export class AddToCartComponent extends Component {
  requiredRefs = ['addToCartButton'];

  /** @type {number[] | undefined} */
  #resetTimeouts = /** @type {number[]} */ ([]);

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('pointerenter', this.#preloadImage);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    if (this.#resetTimeouts) {
      this.#resetTimeouts.forEach(/** @param {number} timeoutId */ (timeoutId) => clearTimeout(timeoutId));
    }
    this.removeEventListener('pointerenter', this.#preloadImage);
  }

  /**
   * Disables the add to cart button.
   */
  disable() {
    this.refs.addToCartButton.disabled = true;
  }

  /**
   * Enables the add to cart button.
   */
  enable() {
    this.refs.addToCartButton.disabled = false;
  }

  /**
   * Handles the click event for the add to cart button.
   * @param {MouseEvent & {target: HTMLElement}} event - The click event.
   */
  handleClick(event) {
    const form = this.closest('form');
    if (!form?.checkValidity()) return;

    // Check if adding would exceed max before animating
    const productForm = /** @type {ProductFormComponent | null} */ (this.closest('product-form-component'));
    const quantitySelector = productForm?.refs.quantitySelector;
    if (quantitySelector?.canAddToCart) {
      const validation = quantitySelector.canAddToCart();
      // Don't animate if it would exceed max
      if (!validation.canAdd) {
        return;
      }
    }
    if (this.refs.addToCartButton.dataset.puppet !== 'true') {
      const animationEnabled = this.dataset.addToCartAnimation === 'true';
      if (animationEnabled && !event.target.closest('.quick-add-modal')) {
        this.#animateFlyToCart();
      }
      this.animateAddToCart();
    }
  }

  #preloadImage = () => {
    const image = this.dataset.productVariantMedia;

    if (!image) return;

    preloadImage(image);
  };

  /**
   * Animates the fly to cart animation.
   */
  #animateFlyToCart() {
    const { addToCartButton } = this.refs;
    const cartIcon = document.querySelector('.header-actions__cart-icon');

    if (!cartIcon || !addToCartButton) return;

    const addToCartButtonRect = addToCartButton.getBoundingClientRect();
    const cartIconRect = cartIcon.getBoundingClientRect();

    const flyToCart = document.createElement('div');
    flyToCart.className = 'fly-to-cart';
    flyToCart.style.cssText = `
      position: fixed;
      z-index: 9999;
      pointer-events: none;
      width: ${addToCartButtonRect.width}px;
      height: ${addToCartButtonRect.height}px;
      border-radius: ${getComputedStyle(addToCartButton).borderRadius};
      background-color: ${getComputedStyle(addToCartButton).backgroundColor};
      left: ${addToCartButtonRect.left}px;
      top: ${addToCartButtonRect.top}px;
      transition: all 0.5s cubic-bezier(0.25, 0.46, 0.45, 0.94);
    `;

    document.body.appendChild(flyToCart);

    requestAnimationFrame(() => {
      flyToCart.style.left = `${cartIconRect.left + cartIconRect.width / 2 - addToCartButtonRect.width / 2}px`;
      flyToCart.style.top = `${cartIconRect.top + cartIconRect.height / 2 - addToCartButtonRect.height / 2}px`;
      flyToCart.style.transform = 'scale(0.2)';
      flyToCart.style.opacity = '0.5';
    });

    setTimeout(() => {
      flyToCart.remove();
    }, 600);
  };

  /**
   * Animates the add to cart button.
   */
  animateAddToCart = async function () {
    const { addToCartButton } = this.refs;

    // Initialize the array if it doesn't exist
    if (!this.#resetTimeouts) {
      this.#resetTimeouts = [];
    }

    // Clear all existing timeouts
    this.#resetTimeouts.forEach(/** @param {number} timeoutId */ (timeoutId) => clearTimeout(timeoutId));
    this.#resetTimeouts = [];

    if (addToCartButton.dataset.added !== 'true') {
      addToCartButton.dataset.added = 'true';
    }

    await onAnimationEnd(addToCartButton);

    // Create new timeout and store it in the array
    const timeoutId = setTimeout(() => {
      addToCartButton.removeAttribute('data-added');

      // Remove this timeout from the array
      const index = this.#resetTimeouts.indexOf(timeoutId);
      if (index > -1) {
        this.#resetTimeouts.splice(index, 1);
      }
    }, 800);

    this.#resetTimeouts.push(timeoutId);
  };
}

/**
 * A custom element that manages a product form.
 *
 * @typedef {object} ProductFormRefs
 * @property {HTMLInputElement} variantId - The form input for submitting the variant ID.
 * @property {AddToCartComponent | undefined} addToCartButtonContainer - The add to cart button container element.
 * @property {HTMLElement | undefined} addToCartTextError - The add to cart text error.
 * @property {HTMLElement | undefined} quantitySelector - The quantity selector element.
 * @property {HTMLElement | undefined} quantityLabel - The quantity label.
 * @property {HTMLElement | undefined} quantityRules - The quantity rules.
 * @property {HTMLElement | undefined} productFormButtons - The product form buttons container.
 * @property {HTMLElement | undefined} volumePricing - The volume pricing element.
 * @extends Component<ProductFormRefs>
 */
export class ProductFormComponent extends Component {
  /** @type {number | undefined} */
  #timeout;

  /** @type {boolean} */
  #isSubmitting = false;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('submit', this.handleSubmit);
    document.addEventListener(ProductSelectEvent.eventName, this.handleProductSelectEvent);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    if (this.#timeout) clearTimeout(this.#timeout);
    this.removeEventListener('submit', this.handleSubmit);
    document.removeEventListener(ProductSelectEvent.eventName, this.handleProductSelectEvent);
  }

  /**
   * Refreshes the cart data.
   * @returns {Promise<object>} The cart data.
   */
  async #refreshCart() {
    const cartItemsComponent = document.querySelector('cart-items-component');
    if (cartItemsComponent) {
      await customElements.whenDefined('cart-items-component');
      return cartItemsComponent.fetchCartData();
    }

    // Fallback for pages without cart-items-component (e.g. page-based cart on product pages)
    return fetch(`${Theme.routes.cart_url}.json`, {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    }).then((response) => {
      if (!response.ok) throw new Error(`Failed to fetch cart: ${response.status}`);
      return response.json();
    });
  }

  /**
   * Sets the live region text for screen readers.
   * @param {string} text - The text to set.
   */
  #setLiveRegionText(text) {
    const liveRegion = this.querySelector('[aria-live]');
    if (liveRegion) {
      liveRegion.textContent = text;
    }
  }

  /**
   * Clears the live region text.
   */
  #clearLiveRegionText() {
    this.#setLiveRegionText('');
  }

  /**
   * Updates the cart quantity display.
   * @param {object} ajaxCart - The cart data from the AJAX response.
   */
  #updateCartQuantity(ajaxCart) {
    const variantIdInput = this.#getVariantIdInput();
    if (!variantIdInput) return;

    const variantId = variantIdInput.value;
    const item = ajaxCart.items?.find((/** @type {{ variant_id: string }} */ i) => String(i.variant_id) === String(variantId));
    const quantitySelector = this.refs.quantitySelector;

    if (item && quantitySelector) {
      quantitySelector.dataset.cartQuantity = String(item.quantity);
    }
  }

  /**
   * Gets the variant ID input element.
   * @returns {HTMLInputElement | null}
   */
  #getVariantIdInput() {
    return this.querySelector('input[name="id"]');
  }

  /** @param {Event} event */
  handleSubmit(event) {
    event.preventDefault();

    if (this.#isSubmitting) return;

    const form = /** @type {HTMLFormElement} */ (event.target);
    if (!form?.checkValidity()) {
      form.reportValidity();
      return;
    }

    const allAddToCartContainers = /** @type {NodeListOf<AddToCartComponent>} */ (
      document.querySelectorAll(`product-form-component[data-product-id="${this.dataset.productId}"] add-to-cart-component`)
    );

    for (const container of allAddToCartContainers) {
      container.disable();
    }

    this.refs.addToCartButtonContainer?.animateAddToCart?.();

    const { addToCartTextError } = this.refs;

    if (this.#timeout) clearTimeout(this.#timeout);

    const formData = new FormData(form);

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    let cartItemComponentsSectionIds = [];
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        cartItemComponentsSectionIds.push(item.dataset.sectionId);
      }
      formData.append('sections', cartItemComponentsSectionIds.join(','));
    });

    const itemCount = Number(formData.get('quantity')) || Number(this.dataset.quantityDefault);
    const deferredEventPromise = CartLinesUpdateEvent.createPromise();

    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: [
          {
            merchandiseId: /** @type {string} */ (formData.get('id')),
            quantity: itemCount,
          },
        ],
        promise: deferredEventPromise.promise,
      })
    );

    const fetchCfg = fetchConfig('javascript', { body: formData });

    fetch(Theme.routes.cart_add_url, {
      ...fetchCfg,
      headers: {
        ...fetchCfg.headers,
        Accept: 'text/html',
      },
    })
      .then((response) => response.json())
      .then(async (response) => {
        if (response.status) {
          this.dispatchEvent(
            new CartErrorEvent({
              error: response.message || 'Add to cart failed',
              code: 'INVALID',
              detail: {
                description: response.description,
                errors: response.errors,
              },
            })
          );

          // Fetch the updated cart to get the actual total quantity for this variant
          this.#refreshCart()
            .then((ajaxCart) =>
              deferredEventPromise.resolve({
                cart: CartLinesUpdateEvent.createCartFromAjaxResponse(ajaxCart),
                detail: {
                  didError: true,
                  items: ajaxCart.items,
                  source: 'product-form-component',
                  sourceId: this.id.toString(),
                  itemCount,
                  productId: this.dataset.productId,
                },
              })
            )
            .catch(deferredEventPromise.reject);

          if (!addToCartTextError) return;
          addToCartTextError.classList.remove('hidden');

          // Reuse the text node if the user is spam-clicking
          const textNode = addToCartTextError.childNodes[2];
          if (textNode) {
            textNode.textContent = response.message;
          } else {
            const newTextNode = document.createTextNode(response.message);
            addToCartTextError.appendChild(newTextNode);
          }

          // Create or get existing error live region for screen readers
          this.#setLiveRegionText(response.message);

          this.#timeout = setTimeout(() => {
            if (!addToCartTextError) return;
            addToCartTextError.classList.add('hidden');

            // Clear the announcement
            this.#clearLiveRegionText();
          }, ERROR_MESSAGE_DISPLAY_DURATION);

          return;
        } else {
          const id = formData.get('id');

          if (addToCartTextError) {
            addToCartTextError.classList.add('hidden');
            addToCartTextError.removeAttribute('aria-live');
          }

          if (!id) throw new Error('Form ID is required');

          // Add aria-live region to inform screen readers that the item was added
          // Get the added text from any add-to-cart button
          const anyAddToCartButton = allAddToCartContainers[0]?.refs.addToCartButton;
          if (anyAddToCartButton) {
            const addedTextElement = anyAddToCartButton.querySelector('.add-to-cart-text--added');
            const addedText = addedTextElement?.textContent?.trim() || Theme.translations.added;

            this.#setLiveRegionText(addedText);

            setTimeout(() => {
              this.#clearLiveRegionText();
            }, SUCCESS_MESSAGE_DISPLAY_DURATION);
          }

          // Fetch the updated cart to get the actual total quantity for this variant
          const cart = await this.#refreshCart()
            .then((ajaxCart) => {
              deferredEventPromise.resolve({
                cart: CartLinesUpdateEvent.createCartFromAjaxResponse(ajaxCart),
                detail: {
                  items: ajaxCart.items,
                  source: 'product-form-component',
                  sourceId: this.id.toString(),
                  itemCount,
                  productId: this.dataset.productId,
                  sections: response.sections,
                  didError: false,
                },
              });

              if (this.#getVariantIdInput()) {
                this.#updateCartQuantity(ajaxCart);
              }

              return ajaxCart;
            })
            .catch(deferredEventPromise.reject);
        }
      })
      .catch((error) => {
        console.error('[product-form] Add to cart error:', error);

        this.dispatchEvent(
          new CartErrorEvent({
            error: 'Add to cart failed',
            code: 'NETWORK_ERROR',
            detail: {
              description: error.message,
            },
          })
        );

        deferredEventPromise.reject(error);

        if (addToCartTextError) {
          addToCartTextError.classList.remove('hidden');
        }

        setTimeout(() => {
          for (const container of allAddToCartContainers) {
            container.enable();
          }
        }, ERROR_BUTTON_REENABLE_DELAY);
      })
      .finally(() => {
        this.#isSubmitting = false;

        for (const container of allAddToCartContainers) {
          container.enable();
        }
      });
  }

  /**
   * Handles the product select event (variant change).
   * @param {ProductSelectEvent} event
   */
  handleProductSelectEvent = (event) => {
    if (event.target !== this) return;

    const { html, sectionId } = event.detail;

    if (!html) return;

    const currentVariantId = this.#getVariantIdInput()?.value;
    const newVariantId = html.querySelector('input[name="id"]')?.value;

    if (currentVariantId === newVariantId) {
      return;
    }

    this.#updateProductForm(html, event);
  };

  /**
   * Updates the product form when the variant changes.
   * @param {Document} html - The new HTML from the Section Rendering API.
   * @param {Event} [event] - The triggering event.
   */
  #updateProductForm(html, event) {
    const quantitySelector = this.refs.quantitySelector;
    const quantityLabel = this.refs.quantityLabel;
    const quantityRules = this.refs.quantityRules;
    const productFormButtons = this.refs.productFormButtons;

    const newProductFormButtons = html.querySelector('product-form-component [ref="productFormButtons"]');
    const newQuantitySelector = html.querySelector('product-form-component [ref="quantitySelector"]');
    const newVariantId = html.querySelector('input[name="id"]');

    // Update the variant ID input
    const currentVariantIdInput = this.#getVariantIdInput();
    if (currentVariantIdInput && newVariantId) {
      currentVariantIdInput.value = newVariantId.value;
    }

    // Update the add-to-cart button
    const currentAddToCartButton = this.querySelector('add-to-cart-component [ref="addToCartButton"]');
    const newAddToCartButton = html.querySelector('product-form-component [ref="addToCartButton"]');
    if (newAddToCartButton && currentAddToCartButton) {
      morph(currentAddToCartButton, newAddToCartButton);
    }

    // Determine if layout is changing (quantity selector appearing/disappearing)
    const isQuantityRulesChanging =
      Boolean(quantityRules) !== Boolean(html.querySelector('.quantity-rules'));
    const isPricePerItemChanging =
      Boolean(this.querySelector('price-per-item')) !== Boolean(html.querySelector('price-per-item'));

    if ((isQuantityRulesChanging || isPricePerItemChanging) && quantitySelector) {
      // Store quantity value before morphing entire container
      const currentQuantityValue = quantitySelector.getValue?.();

      if (productFormButtons && newProductFormButtons) {
        morph(productFormButtons, newProductFormButtons);

        // Get the NEW quantity selector after morphing and update its constraints
        const newQuantityInputElement = /** @type {HTMLInputElement | null} */ (
          html.querySelector('quantity-selector-component input[ref="quantityInput"]')
        );

        // Update elements individually when layout isn't changing
        /** @type {Array<[string, HTMLElement | undefined, HTMLElement | undefined]>} */
        const morphTargets = [
          ['.quantity-label', quantityLabel, quantitySelector],
          ['.quantity-rules', quantityRules, this.refs.productFormButtons],
        ];

        for (const [selector, currentElement, fallback] of morphTargets) {
          this.#morphOrUpdateElement(currentElement, html.querySelector(selector), fallback);
        }
      }
    }

    // Morph volume pricing if it exists
    const currentVolumePricing = this.refs.volumePricing;
    const newVolumePricing = html.querySelector('volume-pricing');
    this.#morphOrUpdateElement(currentVolumePricing, newVolumePricing, this.refs.productFormButtons);

    const hasB2BFeatures =
      html.querySelector('volume-pricing') ||
      html.querySelector('price-per-item');

    if (!hasB2BFeatures) return;

    // Fetch and update cart quantity for the new variant
    this.#refreshCart().then((cart) => this.#updateCartQuantity(cart));
  }

  /**
   * Morphs or removes/adds an element based on current and new element states
   * @param {Element | null | undefined} currentElement - The current element in the DOM
   * @param {Element | null | undefined} newElement - The new element from the server response
   * @param {Element | null} [insertReferenceElement] - Element to insert before if adding new element
   */
  #morphOrUpdateElement(currentElement, newElement, insertReferenceElement = null) {
    if (currentElement && newElement) {
      morph(currentElement, newElement);
    } else if (currentElement && !newElement) {
      currentElement.remove();
    } else if (!currentElement && newElement && insertReferenceElement) {
      insertReferenceElement.insertAdjacentElement('afterend', newElement);
    }
  }
}

if (!customElements.get('add-to-cart-component')) {
  customElements.define('add-to-cart-component', AddToCartComponent);
}

if (!customElements.get('product-form-component')) {
  customElements.define('product-form-component', ProductFormComponent);
}
